/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { If, Then, Else } from "react-if";
import { ErrorMessage, DefaultErrorFallback } from "~/components/Error";
import { useReportError } from "~/services/datadog";
import { noop } from "~/tools";

type ErrorMessageProps = React.ComponentProps<typeof ErrorMessage>;

type Props = ErrorMessageProps & {
  children?: React.ReactNode;
  Component?: React.ComponentType<ErrorMessageProps>;
  fallback?: React.ComponentType<FallbackProps>;
  onReset?: React.ComponentProps<typeof ErrorBoundary>["onReset"];
  resetKeys?: React.ComponentProps<typeof ErrorBoundary>["resetKeys"];
};

const defaultResetKeys: unknown[] = [];

export function ErrorWrap({
  Component = DefaultComponent,
  fallback = DefaultErrorFallback,
  onReset = noop,
  resetKeys = defaultResetKeys,
  ...props
}: Props) {
  const { reportError } = useReportError();

  const myErrorHandler = (error: Error) => {
    reportError(error);
  };

  return (
    <ErrorBoundary
      FallbackComponent={fallback}
      resetKeys={resetKeys}
      onError={myErrorHandler}
      onReset={onReset}
    >
      <If condition={props.error != null}>
        <Then>
          <Component {...props} />
        </Then>
        <Else>{props.children}</Else>
      </If>
    </ErrorBoundary>
  );
}

type DefaultComponentProps = Omit<Props, "Component" | "fallback" | "onReset">;

function DefaultComponent({ children, ...props }: DefaultComponentProps) {
  return (
    <>
      <ErrorMessage {...props} />
      {children}
    </>
  );
}
